import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import api from "../api/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Settings2, LayoutDashboard, Ticket, CheckSquare, FolderKanban, Clock,
  Star, ThumbsUp, BarChart3, Trophy, Users, PhoneCall, MessageSquare, LogOut, Menu, X, Bell, Shield, Headphones, Server, Award, Sparkles,
  Calendar, ClipboardCheck, Wallet, Package
} from "lucide-react";

const nav = [
  { to: "/",              label: "Dashboard",          icon: LayoutDashboard, tabKey: "dashboard" },
  { to: "/projects",      label: "Projects",           icon: FolderKanban,    tabKey: "projects" },
  { to: "/tasks",         label: "Tasks",              icon: CheckSquare,     tabKey: "tasks" },
  { to: "/timesheets",    label: "Timesheets",         icon: Clock,           tabKey: "timesheets" },
  { to: "/tickets",       label: "Tickets",            icon: Ticket,          tabKey: "tickets" },
  { to: "/quality",       label: "Quality",            icon: Star,            tabKey: "quality" },
  { to: "/approvals",     label: "Approvals",          icon: ThumbsUp,        tabKey: "approvals" },
  { to: "/reports",       label: "Reports",            icon: BarChart3,       tabKey: "reports" },
  { to: "/crm",           label: "CRM",                icon: PhoneCall,       tabKey: "crm" },
  { to: "/communications",label: "Communications",     icon: MessageSquare,   tabKey: "communications" },
  { to: "/users",         label: "Users",              icon: Users,           tabKey: "users",               adminOnly: true },
  { to: "/audit",         label: "Audit Logs",         icon: Shield,          tabKey: "audit",               adminOnly: true },
  { to: "/roles-permissions", label: "Roles & Permissions", icon: Shield,    tabKey: "roles-permissions",   adminOnly: true },
  { to: "/comment-policy",     label: "Comment Policy",    icon: Settings2,       tabKey: "comment-policy",      adminOnly: true },
  { to: "/whmcs-tickets",     label: "WHMCS Tickets",     icon: Headphones,      tabKey: "whmcs-tickets" },
  { to: "/whmcs-quality",     label: "WHMCS Quality",     icon: Award,           tabKey: "whmcs-tickets" },
  { to: "/employee-points",   label: "Employee Points",   icon: Trophy,          tabKey: "whmcs-tickets" },
  { to: "/ai-config",         label: "AI Settings",       icon: Sparkles,        tabKey: "ai-config",           adminOnly: true },
  { to: "/whmcs-config",      label: "WHMCS Settings",    icon: Server,          tabKey: "whmcs-config",        adminOnly: true },
  { to: "/my-performance",    label: "My Profile",        icon: Trophy,          tabKey: "my-performance",     alwaysShow: true },
  { to: "/my-requests",       label: "My Requests",       icon: Calendar,        tabKey: "my-requests",        alwaysShow: true },
  { to: "/request-approvals", label: "Request Approvals", icon: ClipboardCheck,  tabKey: "request-approvals",  managerOnly: true },
  { to: "/leave-balances",    label: "Leave Balances",    icon: Wallet,          tabKey: "leave-balances" },
  { to: "/requests-report",   label: "Requests Report",   icon: Clock,           tabKey: "requests-report",    managerOnly: true },
  { to: "/hr",                label: "HR",                icon: Users,           tabKey: "hr",                 hrOnly: true },
  { to: "/service-requests",  label: "Service Requests",  icon: Package,         tabKey: "service-requests",   alwaysShow: true },
];


/* ── Notification Bell ──────────────────────────────────── */
const NotificationBell: React.FC = () => {
  const [open,   setOpen]   = useState(false);
  const [notifs, setNotifs] = useState<any[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setNotifs(r.data.slice(0, 10));
      setUnread(r.data.filter((n: any) => !n.isRead).length);
    } catch {}
  };

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async (id: string) => {
    await api.put(`/notifications/${id}/read`).catch(() => {});
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.put("/notifications/read-all").catch(() => {});
    setNotifs(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(v => !v); if (!open) load(); }}
        className="relative text-gray-500 hover:text-gray-700 p-1">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-9 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-gray-800 text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">Mark all read</button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {notifs.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">No notifications</p>
            ) : notifs.map(n => (
              <div key={n.id} onClick={() => markRead(n.id)}
                className={`px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${!n.isRead ? "bg-blue-50/50" : ""}`}>
                <div className="flex items-start gap-2">
                  {!n.isRead && <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"/>}
                  <div className={!n.isRead ? "" : "ml-4"}>
                    <p className="text-sm text-gray-800 leading-snug">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(n.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Logo = () => (
  <svg width="22" height="22" viewBox="0 0 36 36" fill="none">
    <circle cx="18" cy="18" r="14" stroke="#60A5FA" strokeWidth="2.5" opacity="0.3"/>
    <circle cx="18" cy="18" r="8" stroke="#60A5FA" strokeWidth="2.5" opacity="0.6"/>
    <circle cx="18" cy="18" r="3" fill="#60A5FA"/>
    <line x1="18" y1="0" x2="18" y2="7" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round"/>
    <line x1="18" y1="29" x2="18" y2="36" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round"/>
    <line x1="0" y1="18" x2="7" y2="18" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round"/>
    <line x1="29" y1="18" x2="36" y2="18" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const Layout: React.FC = () => {
  const { user, logout, isAdmin, canAccessTab } = useAuth();
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isManagerRole = isAdmin || user?.role === "MANAGER" || user?.role === "DEPARTMENT_HEAD" || user?.role === "HR";
  const isHrRole = isAdmin || user?.role === "HR";
  const visibleNav = nav.filter(n => {
    if (n.adminOnly && !isAdmin) return false;
    if ((n as any).managerOnly && !isManagerRole) return false;
    if ((n as any).hrOnly && !isHrRole) return false;
    if ((n as any).alwaysShow) return true;
    return canAccessTab(n.tabKey);
  });

  const SidebarBody = ({ showLabels, onNavClick }: { showLabels: boolean; onNavClick?: () => void }) => (
    <>
      <nav className="flex-1 py-3 overflow-y-auto overscroll-contain">
        {visibleNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-gray-800 ${
                isActive ? "bg-blue-600 text-white" : "text-gray-400"
              }`
            }
          >
            <item.icon size={18} />
            {showLabels && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-gray-700" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold shrink-0">
            {user?.name?.[0]}
          </div>
          {showLabels && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-gray-400">{user?.role}</p>
              </div>
              <button onClick={logout} className="text-gray-400 hover:text-white p-1" aria-label="Logout">
                <LogOut size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex ${desktopOpen ? "w-64" : "w-16"} bg-gray-900 text-white flex-col transition-all duration-300 shrink-0`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          {desktopOpen && (
            <div className="flex items-center gap-2">
              <Logo />
              <span className="font-bold text-lg text-blue-400">TrackPoint</span>
            </div>
          )}
          <button onClick={() => setDesktopOpen(!desktopOpen)} className="text-gray-400 hover:text-white p-1" aria-label="Toggle sidebar">
            {desktopOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <SidebarBody showLabels={desktopOpen} />
      </aside>

      {/* Mobile Sidebar Drawer + Backdrop */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50 animate-in fade-in" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={`md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-gray-900 text-white flex flex-col transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
             style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Logo />
            <span className="font-bold text-lg text-blue-400">TrackPoint</span>
          </div>
          <button onClick={() => setMobileOpen(false)} className="text-gray-400 hover:text-white p-1" aria-label="Close menu">
            <X size={22} />
          </button>
        </div>
        <SidebarBody showLabels={true} onNavClick={() => setMobileOpen(false)} />
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2"
                style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setMobileOpen(true)} className="md:hidden text-gray-600 hover:text-gray-900 p-1 -ml-1" aria-label="Open menu">
              <Menu size={22} />
            </button>
            <h1 className="text-base sm:text-xl font-semibold text-gray-800 truncate">TrackPoint</h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <NotificationBell />
            <span className="hidden md:inline text-sm text-gray-600 truncate max-w-[200px]">Welcome, {user?.name}</span>
            <button onClick={logout} className="md:hidden text-gray-500 hover:text-red-600 p-1" aria-label="Logout">
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-3 sm:p-6"
              style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
